import { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
    useLoopBestQuote,
    LoopQuoteExpanded,
    getLoopSlippage,
    useWindQuoteAndIxs,
    LoopRouteResponseExpanded,
    LoopExpanded,
    PriceFetchType,
    useWhirlpoolsOffchainQuery,
    useWhirlpoolByAddresses,
    useOraclePrices,
    getWhirlpoolFromLoopVault,
    useActiveWallet,
    useUserWhirlpoolPositions,
    useTokenListMetadataByMint,
    isWhirlpoolMetadata
} from "@bridgesplit/abf-react";
import { DispatchType } from "@bridgesplit/react";
import { percentUiToDecimals, lamportsToUiAmount, uiAmountToLamports } from "@bridgesplit/utils";
import { DEFAULT_SLIPPAGE } from "app/components/common";
import {
    BaseRouteStepExpanded,
    LoopRouteType,
    TokenListMetadata,
    WhirlpoolOffchain,
    WhirlpoolPositionExpanded
} from "@bridgesplit/abf-sdk";
import { getPoolData } from "app/components/loan/actions/whirlpool/orca";
import { skipToken } from "@reduxjs/toolkit/query";
import { WhirlpoolPoolData } from "app/components/loan/actions/whirlpool/types";
import { sqrtPriceX64ToTickIndex } from "@orca-so/whirlpool-client-sdk";
import { BN } from "bn.js";

import { WindForm } from "./types";
import { useLoopContext } from "../LoopContext";
import { DEFAULT_WIND_FORM } from "./constants";

interface WindContext {
    quote: LoopQuoteExpanded | null | undefined;
    externalQuote: LoopRouteResponseExpanded | undefined;
    collateralAmount: number | undefined; // this is the amount of collateral being deposited into the loop. for most loops this is the same as input amount, but for orca loops it is 1
    leveragedCollateralAmount: number | undefined; // this is the amount of collateral being deposited into the loop. for most loops this is the same as input amount, but for orca loops it is 1
    inputToken: TokenListMetadata | undefined; // the token that is being deposited into the loop. for most loops this is the same as collateral token, but for orca loops it is the principal token
    isQuoteFetching: boolean;
    form: WindForm;
    setForm: DispatchType<WindForm>;
    isActionOpen: boolean;
    setIsActionOpen: DispatchType<boolean>;
    supportedDepositTokens: TokenListMetadata[] | undefined;
    whirlpoolPositions?: WhirlpoolPositionExpanded[] | undefined;
    whirlpoolOffchain?: WhirlpoolOffchain;
}

export function getLoopInputMintFromForm(form: WindForm) {
    if (form.orcaPosition) {
        return form.orcaPosition.position.positionMint;
    }
    return form.inputToken?.mint;
}

const LoopContext = createContext<WindContext | null>(null);

export function useWindContext(): WindContext {
    const context = useContext(LoopContext);
    if (context === null) {
        throw new Error("useWindContext must be used within a LoopProvider");
    }
    return context;
}

export function getCollateralInputAmount(loopExpanded: LoopExpanded | undefined, form: WindForm) {
    if (!loopExpanded) return undefined;
    if (loopExpanded.loopVault.routeType === LoopRouteType.Orca) {
        return 1;
    }
    return form.inputAmount;
}
export function getLeveragedCollateralAmount(
    loopExpanded: LoopExpanded | undefined,
    form: WindForm,
    externalQuote: LoopRouteResponseExpanded | undefined
) {
    if (!loopExpanded || !externalQuote || !form.inputAmount || !form.multiplier) return undefined;
    if (loopExpanded.loopVault.routeType === LoopRouteType.Orca) {
        return 1;
    }

    if (externalQuote.minOutputAmount === 0) {
        return form.inputAmount * (form.multiplier - (form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE));
    }

    return (
        form.inputAmount + lamportsToUiAmount(externalQuote?.minOutputAmount, loopExpanded?.collateralToken.decimals)
    );
}

export function WindProvider({ children }: { children: React.ReactNode }) {
    const { loopExpanded } = useLoopContext();
    const [isActionOpen, setIsActionOpen] = useState(false);
    const [form, setForm] = useState<WindForm>(DEFAULT_WIND_FORM);
    const { user } = useActiveWallet();

    // Fetch whirlpool data for Orca loops
    const whirlpoolData = useLoopWhirlpoolData(loopExpanded);

    const defaultSlippageDecimals = percentUiToDecimals(getLoopSlippage(loopExpanded).defaultSlippageUi);

    useEffect(() => {
        if (loopExpanded && form.percentSlippageDecimals === undefined) {
            setForm({
                ...form,
                percentSlippageDecimals: defaultSlippageDecimals
            });
        }
    }, [defaultSlippageDecimals, form, loopExpanded]);

    // Add effect to set Orca position params
    useEffect(() => {
        if (loopExpanded?.loopVault.routeType === LoopRouteType.Orca && whirlpoolData.whirlpoolOffchain) {
            const routeParams = loopExpanded.loopVault.quoteFetchParams as any;
            const offchain = whirlpoolData.whirlpoolOffchain;

            if (routeParams.type === "orca" && offchain?.tickSpacing && offchain?.tokenMintA && offchain?.tokenMintB) {
                if (!form.orcaPosition) {
                    setForm((prevForm) => ({
                        ...prevForm,
                        inputToken: loopExpanded?.inputToken,
                        loanCreationParams: {
                            OrcaPositionCreate: {
                                upperTick: routeParams.tickUpper,
                                lowerTick: routeParams.tickLower,
                                tickSpacing: offchain.tickSpacing,
                                whirlpool: whirlpoolData.whirlpoolAddress || "",
                                tokenMintA: offchain.tokenMintA,
                                tokenMintB: offchain.tokenMintB
                            }
                        }
                    }));
                } else {
                    setForm((prevForm) => ({
                        ...prevForm,
                        loanCreationParams: "Default"
                    }));
                }
            }
        } else {
            // Reset loan creation params if not an Orca loop
            setForm((prevForm) => ({
                ...prevForm,
                loanCreationParams: "Default",
                inputToken: loopExpanded?.inputToken
            }));
        }
    }, [
        loopExpanded?.loopVault.routeType,
        whirlpoolData.whirlpoolAddress,
        whirlpoolData.whirlpoolOffchain,
        loopExpanded?.loopVault.quoteFetchParams,
        loopExpanded?.inputToken,
        form.orcaPosition
    ]);

    const priceOverride = useMemo(() => {
        return getLoopWindPriceOverride({
            loopExpanded,
            inputAmount: form.inputAmount,
            leverageMultiplier: form.multiplier,
            orcaPosition: form.orcaPosition,
            slippagePctDecimals: form.percentSlippageDecimals ?? defaultSlippageDecimals
        });
    }, [
        loopExpanded,
        form.inputAmount,
        form.multiplier,
        form.percentSlippageDecimals,
        defaultSlippageDecimals,
        form.orcaPosition
    ]);

    const collateralAmount = useMemo(() => {
        return getCollateralInputAmount(loopExpanded, form);
    }, [loopExpanded, form]);

    const loopBestQuote = useLoopBestQuote({
        loopExpanded: loopExpanded,
        leverageMultiplier: form.multiplier && form.multiplier - (form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE),
        collateralAmountUi: getCollateralInputAmount(loopExpanded, form),
        inputAmountUi: form.inputAmount,
        strategyDuration: form.strategyDuration,
        priceOverride,
        orcaPosition: form.orcaPosition
    });

    const routeOverrideParams = useMemo(() => {
        if (loopExpanded?.loopVault.routeType === LoopRouteType.Orca && form.inputToken) {
            if (form.inputToken.mint === loopExpanded.principalToken.mint) {
                return {
                    positionParams: {
                        principalMint: loopExpanded.principalToken.mint,
                        positionPrincipalInput: uiAmountToLamports(
                            form.inputAmount,
                            loopExpanded.principalToken.decimals
                        )
                    }
                } as Partial<BaseRouteStepExpanded>;
            }
            if (form.inputToken?.mint === loopExpanded.collateralToken.mint && form.orcaPosition) {
                return {
                    positionParams: {
                        existingPosition: true,
                        principalMint: loopExpanded.principalToken.mint,
                        positionPrincipalInput: uiAmountToLamports(
                            form.inputAmount,
                            loopExpanded.principalToken.decimals
                        )
                    },
                    tickUpper: sqrtPriceX64ToTickIndex(new BN(form.orcaPosition?.position.upperSqrtPrice)),
                    tickLower: sqrtPriceX64ToTickIndex(new BN(form.orcaPosition?.position.lowerSqrtPrice))
                } as Partial<BaseRouteStepExpanded>;
            }
        }
        return undefined;
    }, [loopExpanded, form.inputToken, form.inputAmount, form.orcaPosition]);

    const externalQuote = useWindQuoteAndIxs({
        loopExpanded: loopExpanded,
        principalAmount: loopBestQuote.data?.principalAmount,
        slippagePercentDecimals: form.percentSlippageDecimals ?? defaultSlippageDecimals,
        inputAmount: form.inputAmount,
        routeOverrideParams,
        orcaPosition: form.orcaPosition
    });

    const leveragedCollateralAmount = useMemo(() => {
        return getLeveragedCollateralAmount(loopExpanded, form, externalQuote.data);
    }, [loopExpanded, form, externalQuote.data]);

    const isQuoteFetching = loopBestQuote.isFetching || externalQuote.isFetching || whirlpoolData.isLoading;

    const supportedDepositTokens = loopExpanded
        ? Array.from(new Set([loopExpanded.inputToken, loopExpanded.collateralToken]))
        : undefined;

    const metadata = useTokenListMetadataByMint(loopExpanded?.collateralToken.mint); // purposefully using collateral token for orca loops
    const isOrcaPosition = isWhirlpoolMetadata(metadata);

    const { data: wp } = useUserWhirlpoolPositions({ skip: !isOrcaPosition });

    const positions = useMemo(
        () =>
            user
                ? wp
                      ?.filter((w) => w.whirlpoolAddress === loopExpanded?.collateralToken.mint && w.totalPrice >= 1)
                      .sort((a, b) => b.totalPrice - a.totalPrice)
                : [],
        [loopExpanded?.collateralToken.mint, user, wp]
    );

    return (
        <LoopContext.Provider
            value={{
                quote: loopBestQuote.data,
                inputToken: loopExpanded?.inputToken,
                form,
                setForm,
                externalQuote: externalQuote.data,
                leveragedCollateralAmount,
                collateralAmount,
                isQuoteFetching,
                isActionOpen,
                setIsActionOpen,
                supportedDepositTokens,
                whirlpoolPositions: positions,
                whirlpoolOffchain: whirlpoolData.whirlpoolOffchain
            }}
        >
            {children}
        </LoopContext.Provider>
    );
}

function getLoopWindPriceOverride({
    loopExpanded,
    inputAmount,
    leverageMultiplier,
    orcaPosition,
    slippagePctDecimals
}: {
    loopExpanded: LoopExpanded | undefined;
    inputAmount: number | undefined;
    leverageMultiplier: number | undefined;
    orcaPosition: WhirlpoolPositionExpanded | undefined;
    slippagePctDecimals: number;
}): number | undefined {
    // Return undefined if any required data is missing or still loading
    if (!loopExpanded || !inputAmount || !leverageMultiplier) {
        return undefined;
    }

    if (loopExpanded.loopVault.routeType === LoopRouteType.Orca) {
        const routeParams = loopExpanded.loopVault.quoteFetchParams as any;
        if (routeParams.type === "orca") {
            if (orcaPosition) {
                return orcaPosition.totalPrice;
            } else {
                return loopExpanded.inputTokenOracle.getUsdAmount(inputAmount);
            }
        }
    }

    return undefined;
}

export function useLoopWhirlpoolData(loopExpanded: LoopExpanded | undefined) {
    const whirlpoolAddress = useMemo(() => {
        if (!loopExpanded || loopExpanded.loopVault.routeType !== LoopRouteType.Orca) {
            return undefined;
        }
        return getWhirlpoolFromLoopVault(loopExpanded.loopVault);
    }, [loopExpanded]);

    const { data: whirlpoolsOffchain, isLoading: isOffchainLoading } = useWhirlpoolsOffchainQuery(
        whirlpoolAddress ? { addresses: [whirlpoolAddress] } : skipToken
    );

    const { getWhirlpool, isLoading: isParsedLoading } = useWhirlpoolByAddresses(
        whirlpoolAddress ? [whirlpoolAddress] : []
    );

    const [poolData, setPoolData] = useState<WhirlpoolPoolData | null | undefined>(undefined);
    const [poolDataLoading, setPoolDataLoading] = useState(false);
    const [poolDataError, setPoolDataError] = useState<Error | null>(null);

    useEffect(() => {
        let mounted = true;

        async function fetchPoolData() {
            if (!whirlpoolAddress) {
                setPoolData(undefined);
                setPoolDataError(null);
                return;
            }

            setPoolDataLoading(true);
            setPoolDataError(null);

            try {
                const data = await getPoolData(whirlpoolAddress);
                if (mounted) {
                    setPoolData(data);
                }
            } catch (error) {
                if (mounted) {
                    setPoolDataError(error as Error);
                    setPoolData(null);
                }
            } finally {
                if (mounted) {
                    setPoolDataLoading(false);
                }
            }
        }

        fetchPoolData();

        return () => {
            mounted = false;
        };
    }, [whirlpoolAddress]);

    const whirlpoolOffchain = useMemo(() => {
        return whirlpoolsOffchain?.data.find((wp) => wp.address === whirlpoolAddress);
    }, [whirlpoolsOffchain?.data, whirlpoolAddress]);

    const parsedWhirlpool = useMemo(() => {
        return whirlpoolAddress ? getWhirlpool(whirlpoolAddress) : undefined;
    }, [getWhirlpool, whirlpoolAddress]);

    const { getOracle, isLoading: isOracleLoading } = useOraclePrices(
        [whirlpoolOffchain?.tokenMintA, whirlpoolOffchain?.tokenMintB],
        PriceFetchType.Spot,
        5_000
    );

    const tokenAOracle = getOracle(whirlpoolOffchain?.tokenMintA, PriceFetchType.Twap);
    const tokenBOracle = getOracle(whirlpoolOffchain?.tokenMintB, PriceFetchType.Twap);

    const isLoading = isParsedLoading || isOffchainLoading || poolDataLoading || isOracleLoading;

    return {
        whirlpoolOffchain,
        poolData,
        parsedWhirlpool,
        poolDataLoading,
        poolDataError,
        whirlpoolAddress,
        isLoading,
        tokenAOracle,
        tokenBOracle
    };
}
